import type { AlertDialogModel } from '@/components/AlertDialog.vue'
import type { ConfirmationServiceMethods, GalleriaResponsiveOptions, ToastMessageOptions, ToastServiceMethods } from 'primevue'
import type { ConfirmationOptions } from 'primevue/confirmationoptions'
import { computed, reactive, ref, type Reactive } from 'vue'
import router from '@/router'
import { useFairStore } from '@/stores/fair'
import i18n from '@/i18n'
import { useSiteStore } from '@/stores/site'

interface KakaoJsSdkParams {
  apiKey: string
  callback?: (() => void) | null
}

interface ZoneTracker {
  IR_CD: string
  REFER_URL: string
  AGENT: string
  AD_COM: string
  CAMP_CODE: string
  ZONE_CODE: string
  USER_ID: string
  IP_ADDR: string
}

export const confirmMessage = ref('')
export const confirmIcon = ref('pi pi-info-circle')

export class Common {
  private readonly alertDialogModel: Reactive<AlertDialogModel>
  private readonly progressPid = ref<number | undefined>()
  public readonly isProgressActive = computed(() => this.progressPid.value !== undefined)
  public readonly locale = ref(i18n.global.locale)
  public readonly t = i18n.global.t
  public showLoading = ref(false)

  constructor(
    private readonly toastService: ToastServiceMethods,
    private readonly confirmService: ConfirmationServiceMethods,
    public readonly galleriaResponsiveOptions: GalleriaResponsiveOptions[]
  ) {
    this.alertDialogModel = reactive({
      visible: false,
      title: '',
      message: '',
      accept: () => {}
    })
  }

  // TODO 메쎄이상 로고이므로, 납품 시에는 아래 로고를 변경해주어야 함
  public getDefaultLogo = () => {
    return 'https://d3ib83x3z60m0e.cloudfront.net/wp-content/uploads/2019/12/05102340/es_ci.png'
  }

  public showLoader = () => {
    this.showLoading.value = true
  }

  public hideLoader = () => {
    this.showLoading.value = false
  }

  public showProgress = (limitSeconds: number = 5) => {
    if (this.progressPid.value !== undefined) {
      window.clearTimeout(this.progressPid.value)
    }

    const progress = document.getElementById('progress')
    progress?.classList.add('active')

    this.progressPid.value = window.setTimeout(() => {
      this.hideProgress()
    }, limitSeconds * 1000)
  }

  public hideProgress = () => {
    window.clearTimeout(this.progressPid.value)
    this.progressPid.value = undefined
    const progress = document.getElementById('progress')
    progress?.classList.remove('active')
  }

  public getAlertDialogModel = () => {
    return this.alertDialogModel
  }

  public nl2br = (text: string) => {
    return text.replace(/\n/g, '<br>')
  }

  /**
   * @see https://primevue.org/toast/
   * @param message
   */
  public toast = (message: ToastMessageOptions) => {
    message.summary = message.summary ?? i18n.global.t('toast.summary')
    message.life = message.life ?? 3000
    this.toastService.add(message)
  }

  /**
   * Alert the user with the given message.
   *
   * @param message the message to show to the user
   */
  public alert = (message: string, title?: string | null, acceptCallback?: () => void) => {
    this.alertDialogModel.message = this.nl2br(message)
    this.alertDialogModel.visible = true
    this.alertDialogModel.title = title ?? i18n.global.t('alert.title')
    this.alertDialogModel.accept = acceptCallback
  }

  public setLocale = (lang: string) => {
    localStorage.setItem('lastLocale', lang)
    this.locale.value = lang
  }

  /**
   * 영문 번역을 제공하지 않는 페이지에서 영문 언어를 선택했을 때 alert
   */
  public showNoTranslationAlert = () => {
    if (this.locale.value == 'ko') {
      return
    }
    this.alert('Sorry, the English version of this page is currently under preparation.')
  }

  /**
   * 행사 설정 미완료 시 alert
   */
  public showNotCompletedSetupAlert = (incompleteFields?: string[], callback?: () => void) => {
    const siteStore = useSiteStore()

    incompleteFields ??= []
    callback ??= () => {
      if (!siteStore.siteConfig.basePath) {
        router.back()
      } else {
        location.href = siteStore.siteConfig.basePath
      }
    }

    let message = i18n.global.t('alert.fairSetupIncomplete') + '<br>' + i18n.global.t('alert.contactSecretariat')

    if (incompleteFields.length > 0) {
      message += `<br><br><span class="!text-[1.1rem]">※ ${i18n.global.t('alert.missingSettings')}</span>`
    }

    incompleteFields.forEach((field) => {
      message += `<br><span class="!text-[1.1rem] ml-1">· ${field}</span>`
    })
    this.alert(message, null, callback)
  }

  /**
   * 신청 기간 이전일 때 alert
   */
  public showNotOpenedAlert = (callback?: () => void) => {
    const siteStore = useSiteStore()

    callback ??= () => {
      if (!siteStore.siteConfig.basePath) {
        router.back()
      } else {
        location.href = siteStore.siteConfig.basePath
      }
    }
    this.alert(i18n.global.t('alert.notOpened'), null, callback)
  }

  /**
   * 특정 메뉴 신청 마감일 때 alert
   */
  public showClosedAlert = (callback?: () => void) => {
    const siteStore = useSiteStore()

    callback ??= () => {
      if (!siteStore.siteConfig.basePath) {
        router.back()
      } else {
        location.href = siteStore.siteConfig.basePath
      }
    }
    this.alert(i18n.global.t('alert.closed') + '<br>' + i18n.global.t('alert.contactSecretariat'), null, callback)
  }

  /**
   * 전시회가 종료되었을 경우
   */
  public showFinishFairAlert = () => {
    const fairStore = useFairStore()
    const message = `${i18n.global.t('alert.fairFinishMessage')}

      <span class="pi pi-phone"></span> ${fairStore.fair?.contactPhoneNo}
      <span class="pi pi-envelope !text-sm me-1"></span> ${fairStore.fair?.contactEmail}
    `
    this.alert(message, null, () => router.back())
  }

  /**
   * Show a confirmation dialog to the user.
   *
   * @param message the message to show to the user
   * @param options the options for the confirmation dialog
   *
   * @returns a promise that resolves to true if the user accepts or false if they reject
   * @see https://primevue.org/confirmdialog/#basic
   */
  public confirm = (message: string, options?: ConfirmationOptions): Promise<boolean | void> => {
    confirmMessage.value = message
    confirmIcon.value = options?.icon || 'pi pi-info-circle'

    return new Promise((resolve) => {
      const value = {
        header: i18n.global.t('confirm.header'),
        icon: confirmIcon.value,
        rejectLabel: i18n.global.t('confirm.rejectLabel'),
        rejectProps: {
          size: 'large',
          severity: 'secondary',
          outlined: true
        },
        acceptLabel: i18n.global.t('confirm.acceptLabel'),
        acceptProps: {
          size: 'large',
          severity: 'primary'
        },
        accept: () => resolve(true),
        reject: () => resolve(false),
        ...(options ?? {}),
        ...{ message }
      }

      console.log(value)
      this.confirmService.require(value)
    })
  }

  public open = (url: string, method: 'get' | 'post' = 'get', target: '_blank' | '_self' | '_parent' | '_top' = '_blank', data: any = {}) => {
    if (method === 'get') {
      window.open(url, target)
    } else {
      const form = document.createElement('form')
      form.method = 'POST'
      form.action = url
      form.target = target
      form.style.display = 'none'

      for (const key in data) {
        const input = document.createElement('input')
        input.type = 'hidden'
        input.name = key
        input.value = data[key]
        form.appendChild(input)
      }

      document.body.appendChild(form)
      form.submit()
      document.body.removeChild(form)
    }
  }

  public focusToError = (formSelector: string) => {
    const element = document.querySelector(formSelector + ' .p-invalid')

    if (element instanceof HTMLElement) {
      const canFocus =
        element.attributes.getNamedItem('readonly') === null &&
        element.attributes.getNamedItem('disabled') === null &&
        element.attributes.getNamedItem('type')?.value !== 'hidden' &&
        !element.classList.contains('p-radiobutton') &&
        !element.classList.contains('p-checkbox') &&
        !element.classList.contains('p-select') &&
        !element.classList.contains('hidden')

      if (canFocus) {
        element.focus()
      } else {
        this.scrollToElement(element.nextSibling as HTMLElement)
      }
    }
  }

  public scrollToElement = (element: HTMLElement, position: 'center' | 'top' = 'center') => {
    if (!element) {
      return
    }

    const offset = 60
    const elementPosition = element.getBoundingClientRect().top

    let offsetPosition: number

    if (position === 'center') {
      // Position the element in the center of the viewport
      const windowHeight = window.innerHeight
      offsetPosition = window.scrollY + elementPosition - windowHeight / 2 + element.clientHeight / 2
    } else {
      // Position at top with a fixed offset
      offsetPosition = window.scrollY + elementPosition - offset
    }

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    })
  }

  public isMobile = () => {
    const userAgent = navigator.userAgent || navigator.vendor || (typeof window !== 'undefined' && (window as any).opera)

    // List of common mobile devices user agents
    const mobileDevices = [
      /Android/i,
      /webOS/i,
      /iPhone/i,
      /iPad/i,
      /iPod/i,
      /BlackBerry/i,
      /Windows Phone/i,
      /Opera Mini/i,
      /IEMobile/i,
      /Mobile/i,
      /Silk/i,
      /Kindle/i,
      /Samsung/i,
      /Nokia/i,
      /HTC/i,
      /Sony/i,
      /LG/i,
      /Motorola/i,
      /MIUI/i,
      /OnePlus/i,
      /Meizu/i,
      /ZTE/i,
      /Huawei/i,
      /Lenovo/i,
      /Asus/i,
      /Alcatel/i,
      /Micromax/i,
      /Panasonic/i,
      /TCL/i,
      /Vivo/i,
      /Realme/i,
      /Oppo/i,
      /Tecno/i,
      /Infinix/i,
      /Google Pixel/i,
      /Xiaomi/i,
      /Redmi/i,
      /Poco/i
    ]

    // Check if the user agent matches any of the mobile devices
    for (const element of mobileDevices) {
      if (element.test(userAgent)) {
        return true
      }
    }

    return false
  }

  public loadKakaoJsSdk = (params: KakaoJsSdkParams) => {
    const scriptId = 'kakao.min.js'
    const scriptElement = document.getElementById(scriptId)
    if (scriptElement) {
      document.head.removeChild(scriptElement) // 스크립트 로드 후 삭제
    }
    const script = document.createElement('script')
    script.id = scriptId
    script.src = `https://t1.kakaocdn.net/kakao_js_sdk/2.1.0/kakao.min.js`
    document.head.appendChild(script)

    script.onload = function () {
      globalThis.Kakao.init(params.apiKey)
      if (params.callback) {
        params.callback()
      }
    }
  }

  public loadEsPay = () => {
    const scriptId = 'espay.min.js'
    const scriptElement = document.getElementById(scriptId)
    if (scriptElement) {
      document.head.removeChild(scriptElement) // 스크립트 로드 후 삭제
    }
    const script = document.createElement('script')
    script.id = scriptId
    script.src = `https://fmsapi.messeesang.com/assets/espay/espay.min.js`
    document.head.appendChild(script)

    script.onload = function () {
      globalThis.IMP.init()
    }
  }

  public extractTextWithinParentheses = (input: string) => {
    const regex = /\(([^)]+)\)/g
    const matches = []
    let match

    while ((match = regex.exec(input)) !== null) {
      matches.push(match[1])
    }

    return matches
  }

  public removeTextWithinParentheses = (input: string) => {
    const regex = /\([^)]*\)/g
    return input.replace(regex, '')
  }

  public getPaymentMethodDescription = (payment_method: number | null): string => {
    if (payment_method == 1) {
      return 'VCard:Card'
    } else if (payment_method == 2) {
      return 'vbank'
    } else {
      return ''
    }
  }

  public getZoneTracker = (): ZoneTracker => {
    const zoneTracker: ZoneTracker = {
      IR_CD: '',
      REFER_URL: '',
      AGENT: '',
      AD_COM: '',
      CAMP_CODE: '',
      ZONE_CODE: '',
      USER_ID: '',
      IP_ADDR: ''
    }

    let irCdArray: string[] = []
    const hasZoneTrackerCookie = document.cookie.split(';').some((item) => item.trim().startsWith('zone_traker'))
    const hasZoneTrackerParams = new URLSearchParams(window.location.search).has('ir_cd')

    if (hasZoneTrackerCookie || hasZoneTrackerParams) {
      const urlParams = new URLSearchParams(window.location.search)

      if (hasZoneTrackerParams) {
        zoneTracker.IR_CD = urlParams.get('ir_cd') ?? ''
        if (document.referrer) {
          zoneTracker.REFER_URL = document.referrer
        }
      } else {
        const cookies = document.cookie?.split('; ').reduce((acc: { [key: string]: string }, cookie) => {
          const [name, value] = cookie.split('=')
          acc[decodeURIComponent(name.trim())] = decodeURIComponent(value)
          return acc
        }, {})

        if (cookies && cookies['zone_traker[ir_cd]']) {
          zoneTracker.IR_CD = cookies['zone_traker[ir_cd]'] || ''
        }

        if (cookies && cookies['zone_traker[refer]']) {
          zoneTracker.REFER_URL = cookies['zone_traker[refer]'] || ''
        }

        if (zoneTracker.IR_CD) {
          if (zoneTracker.IR_CD.includes('|')) {
            irCdArray = zoneTracker.IR_CD.split('|')
          } else {
            irCdArray = zoneTracker.IR_CD.split('@')
          }

          zoneTracker.AD_COM = irCdArray[0] || ''
          zoneTracker.CAMP_CODE = irCdArray[1] || ''
          zoneTracker.ZONE_CODE = irCdArray[2] || ''
          zoneTracker.USER_ID = irCdArray[3] || ''
        }
      }
    }

    if (!zoneTracker.IR_CD) {
      zoneTracker.IR_CD = 'direct'
    }

    zoneTracker.IP_ADDR = ''
    zoneTracker.AGENT = navigator.userAgent

    return zoneTracker
  }

  public getImageUrl = (path: string): string => {
    return path.toLowerCase().startsWith('/uploadfiles') ? 'https://img.esfair.kr/fms' + path : path
  }

  public isKorean = (): boolean => {
    const lang = this.locale.value
    return lang === 'ko' || lang === 'kr'
  }

  public isEnglish = (): boolean => {
    const lang = this.locale.value
    return lang === 'en' || lang === 'us'
  }
}
